import { formatPrice, formatTime, roundPhase, timezoneLabel, type Round } from './model';

/** What the one-line summary of a round shows (see ShortRoundSubline). */
export type SublineModel =
  | { kind: 'window'; window: string }
  | { kind: 'live'; window: string; baseline: string; phase: 'open' | 'pending' }
  | { kind: 'resolved'; window: string; baseline: string; final: string; result?: 'up' | 'down' };

/**
 * Summary line of a round, in the feed's timezone ("21:55–22:00 SGT"). A resolved round with both prices
 * adds baseline, final price and result; an open or pending round with a baseline adds the baseline and
 * its phase. Anything else (upcoming, cancelled, a missing price) shows the window only: the line never
 * fills a missing value.
 */
export const sublineModel = (round: Round, timezone: string, now: number): SublineModel => {
  const window = `${formatTime(round.startAt, timezone)}–${formatTime(round.endAt, timezone)} ${timezoneLabel(timezone)}`;
  const { referencePrice, finalPrice, result } = round;
  if (referencePrice === undefined) return { kind: 'window', window };
  const baseline = formatPrice(referencePrice);
  // The feed can still say "open" after the window ended (a wallet row has no feed poll of its own).
  const fed = roundPhase(round, now);
  const phase = fed === 'open' && now >= round.endAt ? 'pending' : fed;
  if (phase === 'open' || phase === 'pending') return { kind: 'live', window, baseline, phase };
  if (phase === 'resolved' && finalPrice !== undefined)
    return { kind: 'resolved', window, baseline, final: formatPrice(finalPrice), ...(result && { result }) };
  return { kind: 'window', window };
};
